import { getGarnerSpecialtyByDescription } from './garnerSpecialties';
import { SelectOption } from '../../types/form';
import { keyBy } from 'lodash-es';

const GARNER_SUPPORTED_EHRS = new Set(['athena', 'mock-ehr']);

let lastAthenaEhrSpecialtyCache: string | null = null;

export const getGarnerSpecialtyByEhrSpecialtyName = ({
  adapterName,
  ehrSpecialty,
}: {
  adapterName: string | undefined;
  ehrSpecialty: string | undefined;
}): SelectOption | undefined => {
  if (!ehrSpecialty || !adapterName || !GARNER_SUPPORTED_EHRS.has(adapterName?.toLowerCase())) {
    return;
  }

  const ehrSpecialtyFormatted = ehrSpecialty.trim().toLowerCase();
  let mappedSpecialty = getGarnerSpecialtyByDescription(ehrSpecialty);

  if (!mappedSpecialty) {
    const mappedAthenaSpecialty = ehrToGarnerSpecialtyMapByDescription[ehrSpecialtyFormatted];
    mappedSpecialty = mappedAthenaSpecialty
      ? {
          value: mappedAthenaSpecialty.garnerSpecialtyId,
          label: mappedAthenaSpecialty.garnerSpecialtyDescription,
        }
      : undefined;
  }

  if (!mappedSpecialty) {
    lastAthenaEhrSpecialtyCache = null;
    return;
  }

  lastAthenaEhrSpecialtyCache = ehrSpecialtyFormatted;
  return mappedSpecialty;
};

export const getEhrSpecialtyByGarnerSpecialtyId = ({
  adapterName,
  garnerSpecialtyId,
  lastEhrSpecialtySearch,
}: {
  adapterName: string | undefined;
  garnerSpecialtyId: string | undefined;
  lastEhrSpecialtySearch?: string | undefined;
}): SelectOption | undefined => {
  if (
    !garnerSpecialtyId ||
    !adapterName ||
    !GARNER_SUPPORTED_EHRS.has(adapterName?.toLowerCase())
  ) {
    return;
  }

  if (
    lastEhrSpecialtySearch &&
    lastAthenaEhrSpecialtyCache === lastEhrSpecialtySearch.trim().toLowerCase()
  ) {
    return {
      value: garnerSpecialtyId,
      label: lastEhrSpecialtySearch,
    };
  }

  const mappedEhrSpecialty =
    garnerToEhrSpecialtyMapById[garnerSpecialtyId.trim().toLowerCase()]?.athenaSpecialtyName;

  return mappedEhrSpecialty
    ? {
        value: garnerSpecialtyId,
        label: mappedEhrSpecialty,
      }
    : undefined;
};

const ATHENA_GARNER_SPECIALTIES_MAPPING = [
  {
    nucc: '101YA0400X',
    athenaSpecialtyName: 'Addiction (Substance Use Disorder) Counselor',
    garnerSpecialtyId: 'adult_addiction_therapist',
    garnerSpecialtyDescription: 'Addiction Therapist',
  },
  {
    nucc: '103TA0400X',
    athenaSpecialtyName: 'Addiction (Substance Use Disorder) Psychologist',
    garnerSpecialtyId: 'adult_addiction_therapist',
    garnerSpecialtyDescription: 'Addiction Therapist',
  },
  {
    nucc: '163WA0400X',
    athenaSpecialtyName: 'Addiction (Substance Use Disorder) Registered Nurse',
    garnerSpecialtyId: 'adult_addiction_therapist',
    garnerSpecialtyDescription: 'Addiction Therapist',
  },
  {
    nucc: '207LA0401X',
    athenaSpecialtyName: 'Addiction Medicine (Anesthesiology) Physician',
    garnerSpecialtyId: 'adult_addiction_medicine_physician',
    garnerSpecialtyDescription: 'Addiction Medicine Physician',
  },
  {
    nucc: '207QA0401X',
    athenaSpecialtyName: 'Addiction Medicine (Family Medicine) Physician',
    garnerSpecialtyId: 'adult_addiction_medicine_physician',
    garnerSpecialtyDescription: 'Addiction Medicine Physician',
  },
  {
    nucc: '207RA0401X',
    athenaSpecialtyName: 'Addiction Medicine (Internal Medicine) Physician',
    garnerSpecialtyId: 'adult_addiction_medicine_physician',
    garnerSpecialtyDescription: 'Addiction Medicine Physician',
  },
  {
    nucc: '2083A0300X',
    athenaSpecialtyName: 'Addiction Medicine (Preventive Medicine) Physician',
    garnerSpecialtyId: 'adult_addiction_medicine_physician',
    garnerSpecialtyDescription: 'Addiction Medicine Physician',
  },
  {
    nucc: '2084A0401X',
    athenaSpecialtyName: 'Addiction Medicine (Psychiatry & Neurology) Physician',
    garnerSpecialtyId: 'adult_addiction_medicine_physician',
    garnerSpecialtyDescription: 'Addiction Medicine Physician',
  },
  {
    nucc: '2084P0802X',
    athenaSpecialtyName: 'Addiction Psychiatry Physician',
    garnerSpecialtyId: 'adult_addiction_medicine_physician',
    garnerSpecialtyDescription: 'Addiction Medicine Physician',
  },
  {
    nucc: '261QM0855X',
    athenaSpecialtyName: 'Adolescent and Children Mental Health Clinic/Center',
    garnerSpecialtyId: 'pediatric_therapist',
    garnerSpecialtyDescription: 'Pediatric Therapist',
  },
  {
    nucc: '207QA0000X',
    athenaSpecialtyName: 'Adolescent Medicine (Family Medicine) Physician',
    garnerSpecialtyId: 'pediatric_therapist',
    garnerSpecialtyDescription: 'Pediatric Therapist',
  },
  {
    nucc: '207RA0000X',
    athenaSpecialtyName: 'Adolescent Medicine (Internal Medicine) Physician',
    garnerSpecialtyId: 'pediatric_therapist',
    garnerSpecialtyDescription: 'Pediatric Therapist',
  },
  {
    nucc: '261QM0850X',
    athenaSpecialtyName: 'Adult Mental Health Clinic/Center',
    garnerSpecialtyId: 'adult_therapist',
    garnerSpecialtyDescription: 'Therapist',
  },
  {
    nucc: '207KA0200X',
    athenaSpecialtyName: 'Allergy Physician',
    garnerSpecialtyId: 'adult_allergist',
    garnerSpecialtyDescription: 'Allergist',
  },
  {
    nucc: '2084P0804X',
    athenaSpecialtyName: 'Child & Adolescent Psychiatry Physician',
    garnerSpecialtyId: 'pediatric_psychiatrist',
    garnerSpecialtyDescription: 'Pediatric Psychiatrist',
  },
  {
    nucc: '261Q00000X',
    athenaSpecialtyName: 'Clinic/Center',
    garnerSpecialtyId: 'urgent_care_clinic',
    garnerSpecialtyDescription: 'Urgent Care Clinic',
  },
  {
    nucc: '207RC0001X',
    athenaSpecialtyName: 'Clinical Cardiac Electrophysiology Physician',
    garnerSpecialtyId: 'adult_electrophysiologist',
    garnerSpecialtyDescription: 'Electrophysiologist',
  },
  {
    nucc: '207SG0201X',
    athenaSpecialtyName: 'Clinical Genetics (M.D.) Physician',
    garnerSpecialtyId: 'adult_geneticist',
    garnerSpecialtyDescription: 'Geneticist',
  },
  {
    nucc: '207ZC0008X',
    athenaSpecialtyName: 'Clinical Informatics (Pathology) Physician',
    garnerSpecialtyId: 'lab',
    garnerSpecialtyDescription: 'Medical Laboratory',
  },
  {
    nucc: '291U00000X',
    athenaSpecialtyName: 'Clinical Medical Laboratory',
    garnerSpecialtyId: 'lab',
    garnerSpecialtyDescription: 'Medical Laboratory',
  },
  {
    nucc: '207SG0203X',
    athenaSpecialtyName: 'Clinical Molecular Genetics Physician',
    garnerSpecialtyId: 'adult_geneticist',
    garnerSpecialtyDescription: 'Geneticist',
  },
  {
    nucc: '207ZC0006X',
    athenaSpecialtyName: 'Clinical Pathology Physician',
    garnerSpecialtyId: 'lab',
    garnerSpecialtyDescription: 'Medical Laboratory',
  },
  {
    nucc: '207ZP0105X',
    athenaSpecialtyName: 'Clinical Pathology/Laboratory Medicine Physician',
    garnerSpecialtyId: 'lab',
    garnerSpecialtyDescription: 'Medical Laboratory',
  },
  {
    nucc: '103TC0700X',
    athenaSpecialtyName: 'Clinical Psychologist',
    garnerSpecialtyId: 'adult_general_psychologist',
    garnerSpecialtyDescription: 'Psychologist',
  },
  {
    nucc: '208C00000X',
    athenaSpecialtyName: 'Colon & Rectal Surgery Physician',
    garnerSpecialtyId: 'adult_colorectal_surgeon',
    garnerSpecialtyDescription: 'Colorectal Surgeon',
  },
  {
    nucc: '101Y00000X',
    athenaSpecialtyName: 'Counselor',
    garnerSpecialtyId: 'adult_therapist',
    garnerSpecialtyDescription: 'Therapist',
  },
  {
    nucc: '207N00000X',
    athenaSpecialtyName: 'Dermatology Physician',
    garnerSpecialtyId: 'adult_general_dermatologist',
    garnerSpecialtyDescription: 'Dermatologist',
  },
  {
    nucc: '207RE0101X',
    athenaSpecialtyName: 'Endocrinology, Diabetes & Metabolism Physician',
    garnerSpecialtyId: 'adult_general_endocrinologist',
    garnerSpecialtyDescription: 'Endocrinologist',
  },
  {
    nucc: '225XE0001X',
    athenaSpecialtyName: 'Environmental Modification Occupational Therapist',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '224ZE0001X',
    athenaSpecialtyName: 'Environmental Modification Occupational Therapy Assistant',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '2084E0001X',
    athenaSpecialtyName: 'Epilepsy Physician',
    garnerSpecialtyId: 'adult_general_neurologist',
    garnerSpecialtyDescription: 'Neurologist',
  },
  {
    nucc: '225XE1200X',
    athenaSpecialtyName: 'Ergonomics Occupational Therapist',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '2251E1200X',
    athenaSpecialtyName: 'Ergonomics Physical Therapist',
    garnerSpecialtyId: 'adult_physical_therapist',
    garnerSpecialtyDescription: 'Physical Therapist',
  },
  {
    nucc: '103TE1100X',
    athenaSpecialtyName: 'Exercise & Sports Psychologist',
    garnerSpecialtyId: 'adult_general_psychologist',
    garnerSpecialtyDescription: 'Psychologist',
  },
  {
    nucc: '207YS0123X',
    athenaSpecialtyName: 'Facial Plastic Surgery Physician',
    garnerSpecialtyId: 'adult_facial_plastic_surgeon',
    garnerSpecialtyDescription: 'Facial Plastic Surgeon',
  },
  {
    nucc: '207Q00000X',
    athenaSpecialtyName: 'Family Medicine Physician',
    garnerSpecialtyId: 'adult_primary_care_physician',
    garnerSpecialtyDescription: 'Primary Care Physician (PCP)',
  },
  {
    nucc: '103TF0000X',
    athenaSpecialtyName: 'Family Psychologist',
    garnerSpecialtyId: 'adult_general_psychologist',
    garnerSpecialtyDescription: 'Psychologist',
  },
  {
    nucc: '224ZF0002X',
    athenaSpecialtyName: 'Feeding, Eating & Swallowing Occupational Therapy Assistant',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '213ES0103X',
    athenaSpecialtyName: 'Foot & Ankle Surgery Podiatrist',
    garnerSpecialtyId: 'adult_foot_and_ankle_ortho',
    garnerSpecialtyDescription: 'Foot And Ankle Orthopedist',
  },
  {
    nucc: '213ES0131X',
    athenaSpecialtyName: 'Foot Surgery Podiatrist',
    garnerSpecialtyId: 'adult_podiatrist',
    garnerSpecialtyDescription: 'Podiatrist',
  },
  {
    nucc: '2084F0202X',
    athenaSpecialtyName: 'Forensic Psychiatry Physician',
    garnerSpecialtyId: 'adult_psychiatrist',
    garnerSpecialtyDescription: 'Psychiatrist',
  },
  {
    nucc: '103TF0200X',
    athenaSpecialtyName: 'Forensic Psychologist',
    garnerSpecialtyId: 'adult_general_psychologist',
    garnerSpecialtyDescription: 'Psychologist',
  },
  {
    nucc: '207RG0100X',
    athenaSpecialtyName: 'Gastroenterology Physician',
    garnerSpecialtyId: 'adult_general_gastroenterologist',
    garnerSpecialtyDescription: 'Gastroenterologist',
  },
  {
    nucc: '170300000X',
    athenaSpecialtyName: 'Genetic Counselor (M.S.)',
    garnerSpecialtyId: 'adult_genetic_counselor',
    garnerSpecialtyDescription: 'Genetic Counselor',
  },
  {
    nucc: '133VN1101X',
    athenaSpecialtyName: 'Gerontological Nutrition Registered Dietitian',
    garnerSpecialtyId: 'adult_dietitian',
    garnerSpecialtyDescription: 'Dietitian',
  },
  {
    nucc: '225XG0600X',
    athenaSpecialtyName: 'Gerontology Occupational Therapist',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '207WX0009X',
    athenaSpecialtyName: 'Glaucoma Specialist (Ophthalmology) Physician',
    garnerSpecialtyId: 'adult_general_ophthalmologist',
    garnerSpecialtyDescription: 'Ophthalmologist (Eye Doctor)',
  },
  {
    nucc: '207VX0201X',
    athenaSpecialtyName: 'Gynecologic Oncology Physician',
    garnerSpecialtyId: 'adult_gynecologic_oncologist',
    garnerSpecialtyDescription: 'Gynecologic Oncologist',
  },
  {
    nucc: '207VG0400X',
    athenaSpecialtyName: 'Gynecology Physician',
    garnerSpecialtyId: 'adult_general_gynecologist',
    garnerSpecialtyDescription: 'Gynecologist (OBGYN)',
  },
  {
    nucc: '225XH1200X',
    athenaSpecialtyName: 'Hand Occupational Therapist',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '2251H1200X',
    athenaSpecialtyName: 'Hand Physical Therapist',
    garnerSpecialtyId: 'adult_physical_therapist',
    garnerSpecialtyDescription: 'Physical Therapist',
  },
  {
    nucc: '171400000X',
    athenaSpecialtyName: 'Health & Wellness Coach',
    garnerSpecialtyId: 'adult_primary_care_physician',
    garnerSpecialtyDescription: 'Primary Care Physician (PCP)',
  },
  {
    nucc: '261QH0700X',
    athenaSpecialtyName: 'Hearing and Speech Clinic/Center',
    garnerSpecialtyId: 'adult_speech_language_pathologist',
    garnerSpecialtyDescription: 'Speech-Language Pathologist',
  },
  {
    nucc: '237700000X',
    athenaSpecialtyName: 'Hearing Instrument Specialist',
    garnerSpecialtyId: 'adult_general_ent',
    garnerSpecialtyDescription: 'Otolaryngologist (ENT)',
  },
  {
    nucc: 'BKMD100051',
    athenaSpecialtyName: 'Hematology & Oncology - Hospice and Palliative Medicine',
    garnerSpecialtyId: 'adult_hematologist_oncologist',
    garnerSpecialtyDescription: 'Hematologist Oncologist',
  },
  {
    nucc: '207RH0003X',
    athenaSpecialtyName: 'Hematology & Oncology Physician',
    garnerSpecialtyId: 'adult_hematologist_oncologist',
    garnerSpecialtyDescription: 'Hematologist Oncologist',
  },
  {
    nucc: '207RH0000X',
    athenaSpecialtyName: 'Hematology (Internal Medicine) Physician',
    garnerSpecialtyId: 'adult_hematologist_oncologist',
    garnerSpecialtyDescription: 'Hematologist Oncologist',
  },
  {
    nucc: '207ZH0000X',
    athenaSpecialtyName: 'Hematology (Pathology) Physician',
    garnerSpecialtyId: 'adult_hematologist_oncologist',
    garnerSpecialtyDescription: 'Hematologist Oncologist',
  },
  {
    nucc: '207RI0008X',
    athenaSpecialtyName: 'Hepatology Physician',
    garnerSpecialtyId: 'adult_hepatologist',
    garnerSpecialtyDescription: 'Hepatologist',
  },
  {
    nucc: '207RH0005X',
    athenaSpecialtyName: 'Hypertension Specialist Physician',
    garnerSpecialtyId: 'adult_general_cardiologist',
    garnerSpecialtyDescription: 'Cardiologist',
  },
  {
    nucc: '207RI0200X',
    athenaSpecialtyName: 'Infectious Disease Physician',
    garnerSpecialtyId: 'adult_infectious_disease_specialist',
    garnerSpecialtyDescription: 'Infectious Disease Specialist',
  },
  {
    nucc: '106H00000X',
    athenaSpecialtyName: 'Marriage & Family Therapist',
    garnerSpecialtyId: 'adult_therapist',
    garnerSpecialtyDescription: 'Therapist',
  },
  {
    nucc: '224900000X',
    athenaSpecialtyName: 'Mastectomy Fitter',
    garnerSpecialtyId: 'adult_breast_cancer_surgeon',
    garnerSpecialtyDescription: 'Breast Cancer Surgeon',
  },
  {
    nucc: '261QM0801X',
    athenaSpecialtyName: 'Mental Health Clinic/Center (Including Community Mental Health Center)',
    garnerSpecialtyId: 'adult_therapist',
    garnerSpecialtyDescription: 'Therapist',
  },
  {
    nucc: '101YM0800X',
    athenaSpecialtyName: 'Mental Health Counselor',
    garnerSpecialtyId: 'adult_therapist',
    garnerSpecialtyDescription: 'Therapist',
  },
  {
    nucc: '207WX0109X',
    athenaSpecialtyName: 'Neuro-ophthalmology Physician',
    garnerSpecialtyId: 'adult_general_ophthalmologist',
    garnerSpecialtyDescription: 'Ophthalmologist (Eye Doctor)',
  },
  {
    nucc: '225XN1300X',
    athenaSpecialtyName: 'Neurorehabilitation Occupational Therapist',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '207UN0901X',
    athenaSpecialtyName: 'Nuclear Cardiology Physician',
    garnerSpecialtyId: 'adult_general_cardiologist',
    garnerSpecialtyDescription: 'Cardiologist',
  },
  {
    nucc: '2085N0904X',
    athenaSpecialtyName: 'Nuclear Radiology Physician',
    garnerSpecialtyId: 'adult_interventional_radiologist',
    garnerSpecialtyDescription: 'Interventional Radiologist',
  },
  {
    nucc: '133VN1201X',
    athenaSpecialtyName: 'Obesity and Weight Management Nutrition Registered Dietitian',
    garnerSpecialtyId: 'adult_dietitian',
    garnerSpecialtyDescription: 'Dietitian',
  },
  {
    nucc: '207V00000X',
    athenaSpecialtyName: 'Obstetrics & Gynecology Physician',
    garnerSpecialtyId: 'adult_general_gynecologist',
    garnerSpecialtyDescription: 'Gynecologist (OBGYN)',
  },
  {
    nucc: '207VX0000X',
    athenaSpecialtyName: 'Obstetrics Physician',
    garnerSpecialtyId: 'adult_obstetrician',
    garnerSpecialtyDescription: 'Obstetrician (OBGYN)',
  },
  {
    nucc: '225X00000X',
    athenaSpecialtyName: 'Occupational Therapist',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '224Z00000X',
    athenaSpecialtyName: 'Occupational Therapy Assistant',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '133VN1301X',
    athenaSpecialtyName: 'Oncology Nutrition Registered Dietitian',
    garnerSpecialtyId: 'adult_dietitian',
    garnerSpecialtyDescription: 'Dietitian',
  },
  {
    nucc: '207WX0200X',
    athenaSpecialtyName: 'Ophthalmic Plastic and Reconstructive Surgery Physician',
    garnerSpecialtyId: 'adult_general_ophthalmologist',
    garnerSpecialtyDescription: 'Ophthalmologist (Eye Doctor)',
  },
  {
    nucc: '156FX1100X',
    athenaSpecialtyName: 'Ophthalmic Technician/Technologist',
    garnerSpecialtyId: 'adult_general_ophthalmologist',
    garnerSpecialtyDescription: 'Ophthalmologist (Eye Doctor)',
  },
  {
    nucc: '207W00000X',
    athenaSpecialtyName: 'Ophthalmology Physician',
    garnerSpecialtyId: 'adult_general_ophthalmologist',
    garnerSpecialtyDescription: 'Ophthalmologist (Eye Doctor)',
  },
  {
    nucc: '225CX0006X',
    athenaSpecialtyName: 'Orientation and Mobility Training Rehabilitation Counselor',
    garnerSpecialtyId: 'adult_nonsurgical_orthopedic_physician',
    garnerSpecialtyDescription: 'Nonsurgical Orthopedist',
  },
  {
    nucc: '207XX0004X',
    athenaSpecialtyName: 'Orthopaedic Foot and Ankle Surgery Physician',
    garnerSpecialtyId: 'adult_foot_and_ankle_ortho',
    garnerSpecialtyDescription: 'Foot And Ankle Orthopedist',
  },
  {
    nucc: '207XS0106X',
    athenaSpecialtyName: 'Orthopaedic Hand Surgery Physician',
    garnerSpecialtyId: 'adult_hand_surgeon',
    garnerSpecialtyDescription: 'Hand Surgeon',
  },
  {
    nucc: '207XS0117X',
    athenaSpecialtyName: 'Orthopaedic Surgery of the Spine Physician',
    garnerSpecialtyId: 'adult_spine_surgeon',
    garnerSpecialtyDescription: 'Spine Surgeon',
  },
  {
    nucc: '207X00000X',
    athenaSpecialtyName: 'Orthopaedic Surgery Physician',
    garnerSpecialtyId: 'adult_sports_orthopedic_surgeon',
    garnerSpecialtyDescription: 'Sports Orthopedic Surgeon',
  },
  {
    nucc: '207XX0801X',
    athenaSpecialtyName: 'Orthopaedic Trauma Physician',
    garnerSpecialtyId: 'adult_nonspine_neurosurgeon',
    garnerSpecialtyDescription: 'Neurosurgeon',
  },
  {
    nucc: '2251X0800X',
    athenaSpecialtyName: 'Orthopedic Physical Therapist',
    garnerSpecialtyId: 'adult_physiatrist',
    garnerSpecialtyDescription: 'Physiatrist',
  },
  {
    nucc: '207YX0602X',
    athenaSpecialtyName: 'Otolaryngic Allergy Physician',
    garnerSpecialtyId: 'adult_allergist',
    garnerSpecialtyDescription: 'Allergist',
  },
  {
    nucc: '207Y00000X',
    athenaSpecialtyName: 'Otolaryngology Physician',
    garnerSpecialtyId: 'adult_general_ent',
    garnerSpecialtyDescription: 'Otolaryngologist (ENT)',
  },
  {
    nucc: '207YX0905X',
    athenaSpecialtyName: 'Otolaryngology/Facial Plastic Surgery Physician',
    garnerSpecialtyId: 'adult_facial_plastic_surgeon',
    garnerSpecialtyDescription: 'Facial Plastic Surgeon',
  },
  {
    nucc: '332BX2000X',
    athenaSpecialtyName: 'Oxygen Equipment & Supplies (DME)',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '261QP3300X',
    athenaSpecialtyName: 'Pain Clinic/Center',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '207LP2900X',
    athenaSpecialtyName: 'Pain Medicine (Anesthesiology) Physician',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '2081P2900X',
    athenaSpecialtyName: 'Pain Medicine (Physical Medicine & Rehabilitation) Physician',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '2084P2900X',
    athenaSpecialtyName: 'Pain Medicine (Psychiatry & Neurology) Physician',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '208VP0000X',
    athenaSpecialtyName: 'Pain Medicine Physician',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '101YP1600X',
    athenaSpecialtyName: 'Pastoral Counselor',
    garnerSpecialtyId: 'adult_therapist',
    garnerSpecialtyDescription: 'Therapist',
  },
  {
    nucc: '246Q00000X',
    athenaSpecialtyName: 'Pathology Specialist/Technologist',
    garnerSpecialtyId: 'lab',
    garnerSpecialtyDescription: 'Medical Laboratory',
  },
  {
    nucc: '2080A0000X',
    athenaSpecialtyName: 'Pediatric Adolescent Medicine Physician',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '2080P0203X',
    athenaSpecialtyName: 'Pediatric Critical Care Medicine Physician',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '207PP0204X',
    athenaSpecialtyName: 'Pediatric Emergency Medicine (Emergency Medicine) Physician',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '2080P0204X',
    athenaSpecialtyName: 'Pediatric Emergency Medicine (Pediatrics) Physician',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '2080P0205X',
    athenaSpecialtyName: 'Pediatric Endocrinology Physician',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '2080P0206X',
    athenaSpecialtyName: 'Pediatric Gastroenterology Physician',
    garnerSpecialtyId: 'pediatric_pediatric_gastroenterologist',
    garnerSpecialtyDescription: 'Pediatric Gastroenterologist',
  },
  {
    nucc: '363LP0200X',
    athenaSpecialtyName: 'Pediatric Nurse Practitioner',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '2080B0002X',
    athenaSpecialtyName: 'Pediatric Obesity Medicine Physician',
    garnerSpecialtyId: 'pediatric_pediatric_endocrinologist',
    garnerSpecialtyDescription: 'Pediatric Endocrinologist',
  },
  {
    nucc: '207ZP0213X',
    athenaSpecialtyName: 'Pediatric Pathology Physician',
    garnerSpecialtyId: 'pediatric_speech_language_pathologist',
    garnerSpecialtyDescription: 'Pediatric Speech-Language Pathologist',
  },
  {
    nucc: '2085P0229X',
    athenaSpecialtyName: 'Pediatric Radiology Physician',
    garnerSpecialtyId: 'mri_imaging_center',
    garnerSpecialtyDescription: 'Imaging Center',
  },
  {
    nucc: '2080S0010X',
    athenaSpecialtyName: 'Pediatric Sports Medicine Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '2080T0004X',
    athenaSpecialtyName: 'Pediatric Transplant Hepatology Physician',
    garnerSpecialtyId: 'adult_transplant_hepatologist',
    garnerSpecialtyDescription: 'Transplant Hepatologist',
  },
  {
    nucc: '2080P0204X',
    athenaSpecialtyName: 'Pediatrics - Emergency Medicine',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '2080H0002X',
    athenaSpecialtyName: 'Pediatrics - Hospice and Palliative Medicine',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '208000000X',
    athenaSpecialtyName: 'Pediatrics Physician',
    garnerSpecialtyId: 'pediatric_general_pediatrician',
    garnerSpecialtyDescription: 'Pediatrician',
  },
  {
    nucc: '207SG0205X',
    athenaSpecialtyName: 'Ph.D. Medical Genetics Physician',
    garnerSpecialtyId: 'adult_geneticist',
    garnerSpecialtyDescription: 'Geneticist',
  },
  {
    nucc: '202K00000X',
    athenaSpecialtyName: 'Phlebology Physician',
    garnerSpecialtyId: 'adult_vascular_surgeon',
    garnerSpecialtyDescription: 'Vascular Surgeon',
  },
  {
    nucc: '2081H0002X',
    athenaSpecialtyName: 'Physical Medicine & Rehabilitation - Hospice and Palliative Medicine',
    garnerSpecialtyId: 'adult_physiatrist',
    garnerSpecialtyDescription: 'Physiatrist',
  },
  {
    nucc: '208100000X',
    athenaSpecialtyName: 'Physical Medicine & Rehabilitation Physician',
    garnerSpecialtyId: 'adult_physiatrist',
    garnerSpecialtyDescription: 'Physiatrist',
  },
  {
    nucc: '225XP0019X',
    athenaSpecialtyName: 'Physical Rehabilitation Occupational Therapist',
    garnerSpecialtyId: 'adult_occupational_therapist',
    garnerSpecialtyDescription: 'Occupational Therapist',
  },
  {
    nucc: '225100000X',
    athenaSpecialtyName: 'Physical Therapist',
    garnerSpecialtyId: 'adult_physical_therapist',
    garnerSpecialtyDescription: 'Physical Therapist',
  },
  {
    nucc: '225200000X',
    athenaSpecialtyName: 'Physical Therapy Assistant',
    garnerSpecialtyId: 'adult_physical_therapist',
    garnerSpecialtyDescription: 'Physical Therapist',
  },
  {
    nucc: '2086S0122X',
    athenaSpecialtyName: 'Plastic and Reconstructive Surgery Physician',
    garnerSpecialtyId: 'adult_facial_plastic_surgeon',
    garnerSpecialtyDescription: 'Facial Plastic Surgeon',
  },
  {
    nucc: '207YX0007X',
    athenaSpecialtyName: 'Plastic Surgery within the Head & Neck (Otolaryngology) Physician',
    garnerSpecialtyId: 'adult_head_neck_surgical_oncologist',
    garnerSpecialtyDescription: 'Head And Neck Surgeon',
  },
  {
    nucc: '2082S0099X',
    athenaSpecialtyName: 'Plastic Surgery Within the Head and Neck (Plastic Surgery) Physician',
    garnerSpecialtyId: 'adult_head_neck_surgical_oncologist',
    garnerSpecialtyDescription: 'Head And Neck Surgeon',
  },
  {
    nucc: '211D00000X',
    athenaSpecialtyName: 'Podiatric Assistant',
    garnerSpecialtyId: 'adult_podiatrist',
    garnerSpecialtyDescription: 'Podiatrist',
  },
  {
    nucc: '261QP1100X',
    athenaSpecialtyName: 'Podiatric Clinic/Center',
    garnerSpecialtyId: 'adult_podiatrist',
    garnerSpecialtyDescription: 'Podiatrist',
  },
  {
    nucc: '213E00000X',
    athenaSpecialtyName: 'Podiatrist',
    garnerSpecialtyId: 'adult_podiatrist',
    garnerSpecialtyDescription: 'Podiatrist',
  },
  {
    nucc: '2084P0800X',
    athenaSpecialtyName: 'Psychiatry Physician',
    garnerSpecialtyId: 'adult_psychiatrist',
    garnerSpecialtyDescription: 'Psychiatrist',
  },
  {
    nucc: '103TP0814X',
    athenaSpecialtyName: 'Psychoanalysis Psychologist',
    garnerSpecialtyId: 'adult_general_psychologist',
    garnerSpecialtyDescription: 'Psychologist',
  },
  {
    nucc: '102L00000X',
    athenaSpecialtyName: 'Psychoanalyst',
    garnerSpecialtyId: 'adult_psychiatrist',
    garnerSpecialtyDescription: 'Psychiatrist',
  },
  {
    nucc: '103T00000X',
    athenaSpecialtyName: 'Psychologist',
    garnerSpecialtyId: 'adult_general_psychologist',
    garnerSpecialtyDescription: 'Psychologist',
  },
  {
    nucc: '2084P0015X',
    athenaSpecialtyName: 'Psychosomatic Medicine Physician',
    garnerSpecialtyId: 'adult_psychiatrist',
    garnerSpecialtyDescription: 'Psychiatrist',
  },
  {
    nucc: '207RP1001X',
    athenaSpecialtyName: 'Pulmonary Disease Physician',
    garnerSpecialtyId: 'adult_general_pulmonologist',
    garnerSpecialtyDescription: 'Pulmonologist (Lung Doctor)',
  },
  {
    nucc: '261QX0203X',
    athenaSpecialtyName: 'Radiation Oncology Clinic/Center',
    garnerSpecialtyId: 'adult_radiation_oncologist',
    garnerSpecialtyDescription: 'Radiation Oncologist',
  },
  {
    nucc: '2085R0001X',
    athenaSpecialtyName: 'Radiation Oncology Physician',
    garnerSpecialtyId: 'adult_radiation_oncologist',
    garnerSpecialtyDescription: 'Radiation Oncologist',
  },
  {
    nucc: '2085R0205X',
    athenaSpecialtyName: 'Radiological Physics Physician',
    garnerSpecialtyId: 'mri_imaging_center',
    garnerSpecialtyDescription: 'Imaging Center',
  },
  {
    nucc: '213ER0200X',
    athenaSpecialtyName: 'Radiology Podiatrist',
    garnerSpecialtyId: 'adult_podiatrist',
    garnerSpecialtyDescription: 'Podiatrist',
  },
  {
    nucc: '225800000X',
    athenaSpecialtyName: 'Recreation Therapist',
    garnerSpecialtyId: 'adult_physical_therapist',
    garnerSpecialtyDescription: 'Physical Therapist',
  },
  {
    nucc: '133V00000X',
    athenaSpecialtyName: 'Registered Dietitian',
    garnerSpecialtyId: 'adult_dietitian',
    garnerSpecialtyDescription: 'Dietitian',
  },
  {
    nucc: '227900000X',
    athenaSpecialtyName: 'Registered Respiratory Therapist',
    garnerSpecialtyId: 'adult_general_pulmonologist',
    garnerSpecialtyDescription: 'Pulmonologist (Lung Doctor)',
  },
  {
    nucc: '261QR0400X',
    athenaSpecialtyName: 'Rehabilitation Clinic/Center',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '283X00000X',
    athenaSpecialtyName: 'Rehabilitation Hospital',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '283XC2000X',
    athenaSpecialtyName: 'Rehabilitation Hospital - Children',
    garnerSpecialtyId: 'adult_pain_medicine',
    garnerSpecialtyDescription: 'Pain Medicine Specialist',
  },
  {
    nucc: '133VN1005X',
    athenaSpecialtyName: 'Renal Nutrition Registered Dietitian',
    garnerSpecialtyId: 'adult_dietitian',
    garnerSpecialtyDescription: 'Dietitian',
  },
  {
    nucc: '207VE0102X',
    athenaSpecialtyName: 'Reproductive Endocrinology Physician',
    garnerSpecialtyId: 'adult_reproductive_endocrinologist',
    garnerSpecialtyDescription: 'Reproductive Endocrinologist',
  },
  {
    nucc: '207RR0500X',
    athenaSpecialtyName: 'Rheumatology Physician',
    garnerSpecialtyId: 'adult_general_rheumatologist',
    garnerSpecialtyDescription: 'Rheumatologist',
  },
  {
    nucc: '207QS1201X',
    athenaSpecialtyName: 'Sleep Medicine (Family Medicine) Physician',
    garnerSpecialtyId: 'adult_sleep_medicine_specialist',
    garnerSpecialtyDescription: 'Sleep Medicine Specialist',
  },
  {
    nucc: '207RS0012X',
    athenaSpecialtyName: 'Sleep Medicine (Internal Medicine) Physician',
    garnerSpecialtyId: 'adult_sleep_medicine_specialist',
    garnerSpecialtyDescription: 'Sleep Medicine Specialist',
  },
  {
    nucc: '207YS0012X',
    athenaSpecialtyName: 'Sleep Medicine (Otolaryngology) Physician',
    garnerSpecialtyId: 'adult_sleep_medicine_specialist',
    garnerSpecialtyDescription: 'Sleep Medicine Specialist',
  },
  {
    nucc: '2084S0012X',
    athenaSpecialtyName: 'Sleep Medicine (Psychiatry & Neurology) Physician',
    garnerSpecialtyId: 'adult_sleep_neurologist',
    garnerSpecialtyDescription: 'Sleep Medicine Specialist',
  },
  {
    nucc: '173F00000X',
    athenaSpecialtyName: 'Sleep Specialist (PhD)',
    garnerSpecialtyId: 'adult_sleep_medicine_specialist',
    garnerSpecialtyDescription: 'Sleep Medicine Specialist',
  },
  {
    nucc: '2471S1302X',
    athenaSpecialtyName: 'Sonography Radiologic Technologist',
    garnerSpecialtyId: 'adult_interventional_radiologist',
    garnerSpecialtyDescription: 'Interventional Radiologist',
  },
  {
    nucc: '2355S0801X',
    athenaSpecialtyName: 'Speech-Language Assistant',
    garnerSpecialtyId: 'adult_speech_language_pathologist',
    garnerSpecialtyDescription: 'Speech-Language Pathologist',
  },
  {
    nucc: '235Z00000X',
    athenaSpecialtyName: 'Speech-Language Pathologist',
    garnerSpecialtyId: 'adult_speech_language_pathologist',
    garnerSpecialtyDescription: 'Speech-Language Pathologist',
  },
  {
    nucc: '2081P0004X',
    athenaSpecialtyName: 'Spinal Cord Injury Medicine Physician',
    garnerSpecialtyId: 'adult_spine_orthopedist',
    garnerSpecialtyDescription: 'Spine Orthopedist',
  },
  {
    nucc: '133VN1501X',
    athenaSpecialtyName: 'Sports Dietetics Nutrition Registered Dietitian',
    garnerSpecialtyId: 'adult_dietitian',
    garnerSpecialtyDescription: 'Dietitian',
  },
  {
    nucc: '207PS0010X',
    athenaSpecialtyName: 'Sports Medicine (Emergency Medicine) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '207QS0010X',
    athenaSpecialtyName: 'Sports Medicine (Family Medicine) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '207RS0010X',
    athenaSpecialtyName: 'Sports Medicine (Internal Medicine) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '204C00000X',
    athenaSpecialtyName: 'Sports Medicine (Neuromusculoskeletal Medicine) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '207XX0005X',
    athenaSpecialtyName: 'Sports Medicine (Orthopaedic Surgery) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '2081S0010X',
    athenaSpecialtyName: 'Sports Medicine (Physical Medicine & Rehabilitation) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '2083S0010X',
    athenaSpecialtyName: 'Sports Medicine (Preventive Medicine) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '2084S0010X',
    athenaSpecialtyName: 'Sports Medicine (Psychiatry & Neurology) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '213ES0000X',
    athenaSpecialtyName: 'Sports Medicine Podiatrist',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '2251S0007X',
    athenaSpecialtyName: 'Sports Physical Therapist',
    garnerSpecialtyId: 'adult_physical_therapist',
    garnerSpecialtyDescription: 'Physical Therapist',
  },
  {
    nucc: '324500000X',
    athenaSpecialtyName: 'Substance Abuse Rehabilitation Facility',
    garnerSpecialtyId: 'adult_therapist',
    garnerSpecialtyDescription: 'Therapist',
  },
  {
    nucc: '261QR0405X',
    athenaSpecialtyName: 'Substance Use Disorder Rehabilitation Clinic/Center',
    garnerSpecialtyId: 'adult_primary_care_physician',
    garnerSpecialtyDescription: 'Primary Care Physician (PCP)',
  },
  {
    nucc: '2082S0105X',
    athenaSpecialtyName: 'Surgery of the Hand (Plastic Surgery) Physician',
    garnerSpecialtyId: 'adult_hand_surgeon',
    garnerSpecialtyDescription: 'Hand Surgeon',
  },
  {
    nucc: '2086S0105X',
    athenaSpecialtyName: 'Surgery of the Hand (Surgery) Physician',
    garnerSpecialtyId: 'adult_hand_surgeon',
    garnerSpecialtyDescription: 'Hand Surgeon',
  },
  {
    nucc: '208600000X',
    athenaSpecialtyName: 'Surgery Physician',
    garnerSpecialtyId: 'adult_general_surgeon',
    garnerSpecialtyDescription: 'General Surgeon',
  },
  {
    nucc: '246ZC0007X',
    athenaSpecialtyName: 'Surgical Assistant',
    garnerSpecialtyId: 'adult_surgical_gynecologist',
    garnerSpecialtyDescription: 'Surgical Gynecologist',
  },
  {
    nucc: '246ZS0410X',
    athenaSpecialtyName: 'Surgical Technologist',
    garnerSpecialtyId: 'adult_surgical_gynecologist',
    garnerSpecialtyDescription: 'Surgical Gynecologist',
  },
  {
    nucc: '2085R0203X',
    athenaSpecialtyName: 'Therapeutic Radiology Physician',
    garnerSpecialtyId: 'adult_radiation_oncologist',
    garnerSpecialtyDescription: 'Radiation Oncologist',
  },
  {
    nucc: '208G00000X',
    athenaSpecialtyName: 'Thoracic Surgery (Cardiothoracic Vascular Surgery) Physician',
    garnerSpecialtyId: 'adult_vascular_surgeon',
    garnerSpecialtyDescription: 'Vascular Surgeon',
  },
  {
    nucc: '207RT0003X',
    athenaSpecialtyName: 'Transplant Hepatology Physician',
    garnerSpecialtyId: 'adult_transplant_hepatologist',
    garnerSpecialtyDescription: 'Transplant Hepatologist',
  },
  {
    nucc: '204F00000X',
    athenaSpecialtyName: 'Transplant Surgery Physician',
    garnerSpecialtyId: 'adult_transplant_surgeon',
    garnerSpecialtyDescription: 'Transplant Surgeon',
  },
  {
    nucc: '2086S0127X',
    athenaSpecialtyName: 'Trauma Surgery Physician',
    garnerSpecialtyId: 'adult_general_surgeon',
    garnerSpecialtyDescription: 'General Surgeon',
  },
  {
    nucc: '207PE0005X',
    athenaSpecialtyName: 'Undersea and Hyperbaric Medicine (Emergency Medicine) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '2083P0011X',
    athenaSpecialtyName: 'Undersea and Hyperbaric Medicine (Preventive Medicine) Physician',
    garnerSpecialtyId: 'adult_sports_medicine_physician',
    garnerSpecialtyDescription: 'Sports Medicine Physician',
  },
  {
    nucc: '261QU0200X',
    athenaSpecialtyName: 'Urgent Care Clinic/Center',
    garnerSpecialtyId: 'urgent_care_clinic',
    garnerSpecialtyDescription: 'Urgent Care Clinic',
  },
  {
    nucc: '207VF0040X',
    athenaSpecialtyName:
      'Urogynecology and Reconstructive Pelvic Surgery (Obstetrics & Gynecology) Physician',
    garnerSpecialtyId: 'adult_urogynecologist',
    garnerSpecialtyDescription: 'Urogynecologist',
  },
  {
    nucc: '2088F0040X',
    athenaSpecialtyName: 'Urogynecology and Reconstructive Pelvic Surgery (Urology) Physician',
    garnerSpecialtyId: 'adult_urogynecologist',
    garnerSpecialtyDescription: 'Urogynecologist',
  },
  {
    nucc: '208800000X',
    athenaSpecialtyName: 'Urology Physician',
    garnerSpecialtyId: 'adult_general_urologist',
    garnerSpecialtyDescription: 'Urologist',
  },
  {
    nucc: '2085R0204X',
    athenaSpecialtyName: 'Vascular & Interventional Radiology Physician',
    garnerSpecialtyId: 'adult_interventional_radiologist',
    garnerSpecialtyDescription: 'Interventional Radiologist',
  },
  {
    nucc: '2084V0102X',
    athenaSpecialtyName: 'Vascular Neurology Physician',
    garnerSpecialtyId: 'adult_vascular_neurologist',
    garnerSpecialtyDescription: 'Vascular Neurologist (Stroke Specialist)',
  },
  {
    nucc: '2086S0129X',
    athenaSpecialtyName: 'Vascular Surgery Physician',
    garnerSpecialtyId: 'adult_vascular_surgeon',
    garnerSpecialtyDescription: 'Vascular Surgeon',
  },
  {
    nucc: '282NW0100X',
    athenaSpecialtyName: "Women's Hospital",
    garnerSpecialtyId: 'adult_general_gynecologist',
    garnerSpecialtyDescription: 'Gynecologist (OBGYN)',
  },
  {
    nucc: '163WW0000X',
    athenaSpecialtyName: 'Wound Care Registered Nurse',
    garnerSpecialtyId: 'urgent_care_clinic',
    garnerSpecialtyDescription: 'Urgent Care Clinic',
  },
];

const ehrToGarnerSpecialtyMapByDescription = keyBy(ATHENA_GARNER_SPECIALTIES_MAPPING, (specialty) =>
  specialty.athenaSpecialtyName.trim().toLowerCase(),
);

const garnerToEhrSpecialtyMapById = keyBy(ATHENA_GARNER_SPECIALTIES_MAPPING, (specialty) =>
  specialty.garnerSpecialtyId.trim().toLowerCase(),
);
